import { withJsonFormsControlProps } from "@jsonforms/react";
import {
  FormControl,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import { Icon, InputItem, Tooltip } from "pepsico-ds";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useSurveyData } from "../../../hooks/useSurveyData";
import { searchHandler } from "../../../utils/functions";
import "./externalSurveySelect.scss";

// The rule stores the partner/url alongside the id, so the runtime
// (pepl-activity SurveyService) can resolve the survey without another lookup.
const SURVEY_CONFIGURATION_PATH = "survey_configuration";

const SEARCH_KEYS = ["surveyName", "surveyDisplayId"];

const EMPTY_MESSAGE = "No surveys found. Add one in Survey Management first.";

const buildDisplayText = (survey) =>
  survey.surveyName
    ? `${survey.surveyName} (${survey.surveyDisplayId})`
    : survey.surveyDisplayId;

const ExternalSurveySelectComponent = ({
  data,
  path,
  handleChange,
  label,
  required,
  enabled,
  visible,
  uischema,
}) => {
  const { surveys, loading, error, fetchSurveys } = useSurveyData();
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  const selectableSurveys = useMemo(
    () => surveys.filter((survey) => survey.surveyDisplayId),
    [surveys]
  );

  const visibleSurveys = useMemo(
    () => searchHandler(searchTerm, SEARCH_KEYS, selectableSurveys),
    [searchTerm, selectableSurveys]
  );

  const selectedSurvey = useMemo(
    () => selectableSurveys.find((survey) => survey.surveyDisplayId === data),
    [selectableSurveys, data]
  );

  // A saved id may no longer exist in Survey Management, and it may also be
  // filtered out by the current search. Either way MUI needs an option matching
  // the value or it drops the selection, so carry one.
  const needsStoredOption =
    !!data && !visibleSurveys.some((survey) => survey.surveyDisplayId === data);

  const updateValue = (surveyDisplayId) => {
    const survey = selectableSurveys.find(
      (s) => s.surveyDisplayId === surveyDisplayId
    );

    handleChange(path, surveyDisplayId);
    handleChange(
      SURVEY_CONFIGURATION_PATH,
      survey
        ? {
            survey_partner: survey.sourcePartner,
            survey_url: survey.surveyUrl ?? null,
          }
        : undefined
    );
  };

  if (visible === false) {
    return null;
  }

  const disabled = enabled === false || uischema?.readOnly || loading;

  return (
    <FormControl
      fullWidth
      required={required}
      size="small"
      className="external-survey-select display-flex flex-direction-column"
      data-testid="external-survey-select"
      style={{ gap: "2px" }}
    >
      <div className="label-element">
        <span className="label-text $font-xsmall-regular">
          <label htmlFor="external-survey-select-input">{label}</label>
          {uischema?.tooltipText && (
            <Tooltip
              direction={uischema.tooltipDirection ?? "right"}
              text={uischema.tooltipText}
            >
              <Icon
                alt=""
                className="$color-icon-default"
                icon={uischema.tooltipIcon ?? "info"}
                size="small"
                variant="outlined"
              />
            </Tooltip>
          )}
        </span>
        {required && (
          <span className="required-text $font-xxsmall-bold float-right">
            *Required
          </span>
        )}
      </div>

      <Select
        id="external-survey-select-input"
        style={{ fontSize: "13px", maxHeight: "32px" }}
        value={data || ""}
        input={<OutlinedInput notched={false} />}
        displayEmpty
        disabled={disabled}
        onChange={(event) => updateValue(event.target.value)}
        onClose={() => setSearchTerm("")}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <span className="external-survey-select__placeholder">
                {loading ? "Loading surveys..." : "Select"}
              </span>
            );
          }
          return selectedSurvey ? buildDisplayText(selectedSurvey) : selected;
        }}
        MenuProps={{
          // Let the search box hold focus instead of the first option.
          autoFocus: false,
          PaperProps: {
            className: "external-survey-select__menu",
            style: { maxHeight: "20rem" },
          },
        }}
      >
        <ListSubheader className="external-survey-select__search">
          <InputItem
            placeholder="Search..."
            trailingIcon="search"
            inputSize="small"
            className="external-survey-select__search-input"
            data-testid="external-survey-search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            // The menu treats keystrokes as type-ahead and space/enter as a
            // selection, which would swallow what is typed here.
            onKeyDown={(event) => {
              if (event.key !== "Escape") {
                event.stopPropagation();
              }
            }}
          />
        </ListSubheader>

        {needsStoredOption && (
          <MenuItem key={data} value={data}>
            {selectedSurvey ? buildDisplayText(selectedSurvey) : data}
          </MenuItem>
        )}

        {/* Keyed on surveyId, not surveyDisplayId: the display id is the
            partner's own id, and two surveys can carry the same one (QA has
            two on partner id 27). Duplicate keys leave orphaned options behind
            in the menu every time the search term changes. */}
        {visibleSurveys.map((survey) => (
          <MenuItem key={survey.surveyId} value={survey.surveyDisplayId}>
            {buildDisplayText(survey)}
          </MenuItem>
        ))}

        {!visibleSurveys.length && !needsStoredOption && (
          <MenuItem disabled data-testid="external-survey-no-match">
            {searchTerm ? "No surveys match that search." : EMPTY_MESSAGE}
          </MenuItem>
        )}
      </Select>

      {error && (
        <p
          className="external-survey-select__error"
          data-testid="external-survey-error"
        >
          Failed to load surveys from Survey Management.
        </p>
      )}

      {!error && !loading && !selectableSurveys.length && (
        <p
          className="external-survey-select__empty"
          data-testid="external-survey-empty"
        >
          {EMPTY_MESSAGE}
        </p>
      )}
    </FormControl>
  );
};

ExternalSurveySelectComponent.propTypes = {
  data: PropTypes.string,
  path: PropTypes.string.isRequired,
  handleChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  required: PropTypes.bool,
  enabled: PropTypes.bool,
  visible: PropTypes.bool,
  uischema: PropTypes.shape({
    readOnly: PropTypes.bool,
    tooltipText: PropTypes.string,
    tooltipDirection: PropTypes.string,
    tooltipIcon: PropTypes.string,
  }),
};

export const ExternalSurveySelectControl = withJsonFormsControlProps(
  ExternalSurveySelectComponent
);

export default ExternalSurveySelectComponent;
